import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Flex,
  FlexItem,
  Label,
  MenuToggle,
  type MenuToggleElement,
  Pagination,
  SearchInput,
  Select,
  SelectList,
  SelectOption,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
  Tooltip,
} from '@patternfly/react-core';
import { ActionsColumn, type IAction, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { CheckCircleIcon, ExclamationCircleIcon, SearchIcon, UserTagIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import type { BackupAttributionCandidate } from '../../../types/models/BackupVmMapping';
import type { BackupAttributionQueueFilters } from '../../../hooks/useBackupAttributionQueue';
import { formatWallClockDateTime } from '../../../utils/dateFormat';
import { SuggestionStatusLabel, suggestionProposesClient } from './backupSuggestionLabels';

const SUBTLE_TEXT = { color: 'var(--pf-t--global--text--color--subtle)' };
const DIRECTORY_ALL = 'all';

// The attribute button's label is the client name alone, capped and ellipsised: a long name
// (`Italique SRL - Ancalagon`) would otherwise widen that one row's action cell far past its
// neighbours' and push the kebab onto a second line. The full name stays reachable in the
// adjacent "NetBox suggests" column, the button's tooltip, and its aria-label.
const CLIENT_NAME_STYLE: React.CSSProperties = {
  display: 'inline-block',
  maxWidth: '11rem',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  verticalAlign: 'bottom',
};

export interface DirectoryOption {
  id: number;
  label: string;
}

interface BackupAttributionQueueTabProps {
  candidates: BackupAttributionCandidate[];
  loading: boolean;
  error: string | null;
  netboxAvailable: boolean;
  netboxReason: string | null;
  truncated: boolean;
  filters: BackupAttributionQueueFilters;
  onFiltersChange: (next: BackupAttributionQueueFilters) => void;
  /** Every `backup_mapping_id` seen so far, labelled where the overrides list knows the names. */
  directoryOptions: DirectoryOption[];
  onRetry: () => void;
  /** Accept the suggestion for one row: one click, one row, one POST. */
  onQuickAttribute: (candidate: BackupAttributionCandidate) => void;
  /** `server_backup_id` of the row whose one-click attribution is in flight, or null. */
  attributingId: number | null;
  /** Open the modal with the client typeahead (suggestion absent, or wrong). */
  onAttributeManually: (candidate: BackupAttributionCandidate) => void;
  /** Open the edit modal for a row that already carries an override (only reachable under `all=1`). */
  onEditOverride: (candidate: BackupAttributionCandidate) => void;
  /** Whether that override row has actually been loaded, so the Edit action can work. */
  canEditOverride: (candidate: BackupAttributionCandidate) => boolean;
}

/**
 * The review queue — the working surface of the backup attribution screen.
 *
 * Every action here is deliberately PER ROW. There is no select-all, no bulk apply, and no
 * "accept all suggestions": NetBox is wrong for exactly the VMs this screen exists to fix (the
 * agent stamps each VM with its hypervisor's owner), so a bulk apply would move paying customers'
 * backups into the house tenant in one click.
 *
 * The three scope filters are server-side and refetch; the search box and pagination are applied
 * client-side over the returned set — the endpoint returns the whole (capped) queue in one call.
 */
function BackupAttributionQueueTab({
  candidates,
  loading,
  error,
  netboxAvailable,
  netboxReason,
  truncated,
  filters,
  onFiltersChange,
  directoryOptions,
  onRetry,
  onQuickAttribute,
  attributingId,
  onAttributeManually,
  onEditOverride,
  canEditOverride,
}: BackupAttributionQueueTabProps) {
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isDirectoryOpen, setIsDirectoryOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchValue);
      setPage(1);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchValue]);

  // A server-side filter change replaces the whole set, so the current page may no longer exist.
  useEffect(() => {
    setPage(1);
  }, [filters.backupMappingId, filters.all, filters.includeMissing]);

  const filteredCandidates = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (q === '') {
      return candidates;
    }
    return candidates.filter(
      (candidate) =>
        candidate.vm_name.toLowerCase().includes(q) ||
        candidate.hypervisor.toLowerCase().includes(q) ||
        (candidate.current_client_name ?? '').toLowerCase().includes(q) ||
        (candidate.suggestion.client_name ?? '').toLowerCase().includes(q) ||
        (candidate.suggestion.owner_name ?? '').toLowerCase().includes(q),
    );
  }, [candidates, debouncedSearch]);

  const paginatedCandidates = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredCandidates.slice(start, start + perPage);
  }, [filteredCandidates, page, perPage]);

  // Attributing a row drops it from the queue, which can leave `page` past the end.
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredCandidates.length / perPage));
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredCandidates.length, perPage, page]);

  const onSetPage = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, pageNumber: number) => {
    setPage(pageNumber);
  };

  const onPerPageSelect = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const clearSearch = () => {
    setSearchValue('');
    // Clear the debounced value too, so the table recovers immediately instead of after the tick.
    setDebouncedSearch('');
    setPage(1);
  };

  const directoryLabel =
    filters.backupMappingId === null
      ? 'All directories'
      : (directoryOptions.find((option) => option.id === filters.backupMappingId)?.label ??
        `Directory #${filters.backupMappingId}`);

  const rowActions = (candidate: BackupAttributionCandidate): IAction[] => {
    if (candidate.has_override) {
      const editable = canEditOverride(candidate);
      return [
        {
          title: 'Edit attribution…',
          // A hard-disabled menu item is not hoverable, so its tooltip would never show — use
          // isAriaDisabled + tooltipProps to keep the reason reachable.
          ...(editable
            ? { onClick: () => onEditOverride(candidate) }
            : {
                isAriaDisabled: true,
                tooltipProps: { content: 'Open the Overrides tab to load and edit this attribution.' },
              }),
        },
      ];
    }
    return [
      {
        title: 'Attribute to…',
        onClick: () => onAttributeManually(candidate),
      },
    ];
  };

  const renderTable = () => (
    <Table aria-label="Backup attribution review queue" variant="compact">
      <Thead>
        <Tr>
          <Th>Virtual machine</Th>
          <Th>Currently attributed to</Th>
          <Th>NetBox suggests</Th>
          <Th>Last backup</Th>
          <Th screenReaderText="Row actions" />
        </Tr>
      </Thead>
      <Tbody>
        {paginatedCandidates.map((candidate) => {
          const { suggestion } = candidate;
          const proposes = suggestionProposesClient(suggestion);
          return (
            <Tr key={candidate.server_backup_id}>
              <Td dataLabel="Virtual machine">
                <strong>{candidate.vm_name}</strong>
                {candidate.retired && (
                  <>
                    {' '}
                    <Label color="grey" isCompact>
                      Retired
                    </Label>
                  </>
                )}
                {candidate.missing_since !== null && (
                  <>
                    {' '}
                    <Label status="warning" isCompact>
                      Missing
                    </Label>
                  </>
                )}
                <div style={SUBTLE_TEXT}>{candidate.hypervisor}</div>
              </Td>
              <Td dataLabel="Currently attributed to">
                {candidate.current_client_name ?? <span style={SUBTLE_TEXT}>Unattributed</span>}
                {candidate.has_override && (
                  <div>
                    <Label color="blue" isCompact>
                      Override
                    </Label>
                  </div>
                )}
              </Td>
              <Td dataLabel="NetBox suggests">
                {proposes && <div>{suggestion.client_name}</div>}
                <SuggestionStatusLabel suggestion={suggestion} hypervisor={candidate.hypervisor} />
                {/* The NetBox owner is a DIFFERENT namespace from the Manager client above — both
                    are shown so staff can sanity-check the mapping before accepting it. */}
                {suggestion.owner_name !== null && (
                  <div style={SUBTLE_TEXT}>NetBox owner: {suggestion.owner_name}</div>
                )}
              </Td>
              <Td dataLabel="Last backup">{formatWallClockDateTime(candidate.last_backup_at)}</Td>
              {/* `modifier="nowrap"` plus the nowrap Flex keeps the attribute button and the kebab
                  on one line: without them a long client name wraps the kebab onto a second row,
                  and the row heights stop matching down the table. */}
              <Td isActionCell modifier="nowrap">
                <Flex
                  flexWrap={{ default: 'nowrap' }}
                  alignItems={{ default: 'alignItemsCenter' }}
                  justifyContent={{ default: 'justifyContentFlexEnd' }}
                  spaceItems={{ default: 'spaceItemsSm' }}
                >
                  {/* One click, one row, one POST — no confirm dialog, because it is reversible from
                      the Overrides tab. Suppressed once a row already carries an override: a second
                      POST for the same triple is rejected by the backend, so the kebab offers Edit.
                      The icon carries the "attribute to" verb (spelled out in the tooltip and the
                      aria-label) so the visible label is just the client name — a bounded, truncated
                      width instead of one that grows with every client's name. */}
                  {proposes && !candidate.has_override && (
                    <FlexItem>
                      <Tooltip content={`Attribute ${candidate.vm_name} to ${suggestion.client_name}`}>
                        <Button
                          variant="primary"
                          size="sm"
                          icon={<UserTagIcon />}
                          aria-label={`Attribute ${candidate.vm_name} to ${suggestion.client_name}`}
                          isLoading={attributingId === candidate.server_backup_id}
                          spinnerAriaValueText="Attributing"
                          isDisabled={attributingId !== null}
                          onClick={() => onQuickAttribute(candidate)}
                        >
                          <span style={CLIENT_NAME_STYLE}>{suggestion.client_name}</span>
                        </Button>
                      </Tooltip>
                    </FlexItem>
                  )}
                  <FlexItem>
                    <ActionsColumn items={rowActions(candidate)} />
                  </FlexItem>
                </Flex>
              </Td>
            </Tr>
          );
        })}
      </Tbody>
    </Table>
  );

  const renderBody = () => {
    if (candidates.length === 0) {
      // Under `all=1` an empty result means the scope itself is empty, so the "show all" nudge
      // would be a dead end.
      return filters.all ? (
        <EmptyState titleText="No backup subjects in this scope" icon={SearchIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>
            No reported backup subjects match the current directory scope. Try a different directory, or include
            subjects whose directory has vanished.
          </EmptyStateBody>
        </EmptyState>
      ) : (
        <EmptyState titleText="Nothing to review" icon={CheckCircleIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>
            Every backed-up VM is attributed, and NetBox agrees. Show all subjects to review the settled ones too.
          </EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => onFiltersChange({ ...filters, all: true })}>
                Show all subjects
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    return (
      <>
        {filteredCandidates.length === 0 ? (
          <EmptyState titleText="No virtual machines match your search" icon={SearchIcon} variant="lg" headingLevel="h2">
            <EmptyStateBody>
              Try a different VM name, hypervisor or client name, or clear the filter to see the whole queue.
            </EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>
                <Button variant="primary" onClick={clearSearch}>
                  Clear filter
                </Button>
              </EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        ) : (
          renderTable()
        )}

        {filteredCandidates.length > 0 && (
          <Pagination
            itemCount={filteredCandidates.length}
            perPage={perPage}
            page={page}
            onSetPage={onSetPage}
            onPerPageSelect={onPerPageSelect}
            variant="bottom"
          />
        )}
      </>
    );
  };

  if (loading) {
    return <CenteredSpinner />;
  }

  if (error !== null) {
    return (
      <EmptyState
        titleText="Unable to load the review queue"
        icon={ExclamationCircleIcon}
        variant="lg"
        headingLevel="h2"
        status="danger"
      >
        <EmptyStateBody>{error}</EmptyStateBody>
        <EmptyStateFooter>
          <EmptyStateActions>
            <Button variant="primary" onClick={onRetry}>
              Retry
            </Button>
          </EmptyStateActions>
        </EmptyStateFooter>
      </EmptyState>
    );
  }

  // The toolbar stays mounted through every non-loading state — including the empty ones — because
  // the scope filters are exactly what a staff member needs when a filtered queue comes back empty.
  return (
    <>
      {/* A NetBox outage is a degraded success, not an error: the queue still renders and staff can
          still attribute manually, so this is a banner over a working table — never an error state. */}
      {!netboxAvailable && (
        <Alert variant="warning" isInline title="NetBox is unavailable" className="pf-v6-u-mb-md">
          No suggestions could be loaded{netboxReason !== null ? ` (${netboxReason})` : ''}. You can still attribute
          VMs manually.
        </Alert>
      )}
      {truncated && (
        <Alert variant="info" isInline title="This queue is truncated" className="pf-v6-u-mb-md">
          The backend returned the first 1000 subjects only. Filter by directory to review the rest — until then,
          an empty-looking queue does not mean everything is reviewed.
        </Alert>
      )}

      <Toolbar>
        <ToolbarContent>
          <ToolbarItem>
            <SearchInput
              placeholder="Search by VM, hypervisor or client..."
              value={searchValue}
              onChange={(_event, value) => setSearchValue(value)}
              onClear={clearSearch}
            />
          </ToolbarItem>
          <ToolbarItem>
            <Select
              id="queue-directory-filter"
              isOpen={isDirectoryOpen}
              selected={filters.backupMappingId === null ? DIRECTORY_ALL : filters.backupMappingId}
              onSelect={(_event, value) => {
                onFiltersChange({
                  ...filters,
                  backupMappingId: value === DIRECTORY_ALL ? null : Number(value),
                });
                setIsDirectoryOpen(false);
              }}
              onOpenChange={(open) => setIsDirectoryOpen(open)}
              toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                <MenuToggle
                  ref={toggleRef}
                  onClick={() => setIsDirectoryOpen((open) => !open)}
                  isExpanded={isDirectoryOpen}
                  aria-label="Filter by backup directory"
                >
                  {directoryLabel}
                </MenuToggle>
              )}
              shouldFocusToggleOnSelect
            >
              <SelectList>
                <SelectOption value={DIRECTORY_ALL}>All directories</SelectOption>
                {directoryOptions.map((option) => (
                  <SelectOption key={option.id} value={option.id}>
                    {option.label}
                  </SelectOption>
                ))}
              </SelectList>
            </Select>
          </ToolbarItem>
          <ToolbarItem>
            <Checkbox
              id="queue-show-all"
              label="Show all subjects"
              isChecked={filters.all}
              onChange={(_event, checked) => onFiltersChange({ ...filters, all: checked })}
            />
          </ToolbarItem>
          <ToolbarItem>
            <Checkbox
              id="queue-include-missing"
              label="Include missing"
              isChecked={filters.includeMissing}
              onChange={(_event, checked) => onFiltersChange({ ...filters, includeMissing: checked })}
            />
          </ToolbarItem>
          <ToolbarItem variant="pagination">
            <Pagination
              itemCount={filteredCandidates.length}
              perPage={perPage}
              page={page}
              onSetPage={onSetPage}
              onPerPageSelect={onPerPageSelect}
            />
          </ToolbarItem>
        </ToolbarContent>
      </Toolbar>

      {renderBody()}
    </>
  );
}

export default BackupAttributionQueueTab;
